import type { ExtranetPackage } from 'app/typings/packages';
import { isValidDiscount } from 'app/utils/price';
import { assertNever } from 'app/utils/typing';

export type RuleType = {
  key: 'standardDiscount' | 'lastMinuteDiscount';
  title: string;
  icon?: React.ReactElement;
  information?: string;
  pkgId: number;
  fieldName?: (string | number)[];
};

export type PricingRuleFormValues = {
  packageRules: {
    packageId: number;
    discount?: number;
    lastMinuteDiscountValue?: number;
    lastMinuteDays?: number;
  }[];
};

type PackageRuleValues = {
  discount?: number;
  lastMinuteDays?: number;
  lastMinuteDiscountValue?: number;
};

export type RuleKeys = RuleType['key'];

export const disableableLabel = (label: string, cond: boolean) => (
  <label
    style={{
      color: cond ? 'rgba(0, 0, 0, 0.85)' : '#8c8c8c',
    }}
  >
    {label}
  </label>
);

export const getInitialFormValues = (
  pkgs: ExtranetPackage[]
): PricingRuleFormValues => ({
  packageRules: pkgs.map((pkg) => ({
    packageId: pkg.id,
    discount: pkg.autoDiscountRate && Math.trunc(pkg.autoDiscountRate * 100),
    lastMinuteDays: pkg.lastMinuteAutoDiscountDays,
    lastMinuteDiscountValue:
      pkg.lastMinuteAutoDiscountRate &&
      Math.trunc(pkg.lastMinuteAutoDiscountRate * 100),
  })),
});

export const renderPricingRuleError = (
  key: RuleKeys,
  isRuleActive: boolean,
  formValues: PricingRuleFormValues,
  packages: ExtranetPackage[],
  isRuleInvalid: boolean,
  pkgId: number
) => {
  switch (key) {
    case 'standardDiscount':
      return renderDefaultDiscountErrors(
        isRuleActive,
        formValues,
        packages,
        pkgId,
        isRuleInvalid
      );

    case 'lastMinuteDiscount':
      return renderLastMinuteErrors(
        isRuleActive,
        formValues,
        packages,
        isRuleInvalid,
        pkgId
      );
    default:
      assertNever(key);
  }
};

export const renderDefaultDiscountErrors = (
  isRuleActive: boolean,
  formValues: PricingRuleFormValues,
  packages: ExtranetPackage[],
  pkgId: number,
  isRuleInvalid: boolean
) => {
  const dayPackage = packages.find((p) => p.id === pkgId)?.dayPackage;

  // This feels like there's a bug here. Either the formValues is not typed correctly or the discount is not typed correctly.
  const discount = (formValues as unknown as { discount?: number })?.discount;

  const emptyRateError =
    isRuleActive &&
    (Math.trunc(discount ?? NaN) === null ||
      Math.trunc(discount ?? NaN) === undefined) ? (
      <div>
        Discount rate is required if you want to last minute activate pricing
        rule
      </div>
    ) : (
      <></>
    );

  const invalidRateError = isRuleInvalid ? (
    <div>
      {`Discount must be between ${dayPackage ? '0%' : '-18%'}
        and -70% of crossed rate`}
    </div>
  ) : (
    <></>
  );

  if (
    (isRuleActive &&
      (Math.trunc(discount ?? NaN) === null ||
        Math.trunc(discount ?? NaN) === undefined)) ||
    isRuleInvalid
  ) {
    return (
      <div className="form-errors">
        {emptyRateError}
        {invalidRateError}
      </div>
    );
  }

  return <></>;
};

const renderLastMinuteErrors = (
  isRuleActive: boolean,
  formValues: PricingRuleFormValues,
  packages: ExtranetPackage[],
  isRuleInvalid: boolean,
  pkgId: number
) => {
  if (!formValues || !formValues.packageRules) {
    return <></>;
  }

  const pkgIndex = formValues.packageRules.findIndex(
    (pr) => pr.packageId === pkgId
  );

  const dayPackage = packages.find((p) => p.id === pkgId)?.dayPackage;

  const lmDaysValue = formValues?.packageRules[pkgIndex].lastMinuteDays;

  const lmRateValue =
    formValues?.packageRules[pkgIndex].lastMinuteDiscountValue;

  const emptyDaysError =
    isRuleActive &&
    (lmDaysValue === undefined || lmDaysValue === null || lmDaysValue === 0) ? (
      <div>
        Number of days is required if you want to activate a last minute pricing
        rule
      </div>
    ) : (
      <></>
    );

  const emptyRateError =
    isRuleActive &&
    (lmRateValue === undefined ||
      Math.trunc(lmRateValue) === null ||
      Math.trunc(lmRateValue) === undefined) ? (
      <div>
        Discount rate is required if you want to last minute activate pricing
        rule
      </div>
    ) : (
      <></>
    );

  const invalidRateError = isRuleInvalid ? (
    <div>
      {`Discount must be between ${dayPackage ? '0%' : '-18%'}
          and -70% of crossed rate`}
    </div>
  ) : (
    <></>
  );

  return (
    <div className="form-errors">
      {emptyDaysError}
      {emptyRateError}
      {invalidRateError}
    </div>
  );
};

export const canSubmitLastMinute = (
  activeRules: boolean,
  formValues: PackageRuleValues,
  packages: ExtranetPackage[],
  pkgId: number,
  lastMinuteAutoDiscountRate: number,
  lastMinuteAutoDiscountDays: number,
  initialDisplayValue: boolean
) => {
  const dayPackage = packages.find((p) => p.id === pkgId)?.dayPackage;

  const activateWithoutValue =
    activeRules &&
    (Math.trunc(formValues?.lastMinuteDays ?? NaN) === null ||
      Math.trunc(formValues?.lastMinuteDays ?? NaN) === undefined ||
      Math.trunc(formValues?.lastMinuteDiscountValue ?? NaN) === null ||
      Math.trunc(formValues?.lastMinuteDiscountValue ?? NaN) === undefined);

  const activateWithoutValidValue =
    (activeRules && formValues?.lastMinuteDays === 0) ||
    (packages.length &&
      activeRules &&
      !isValidDiscount(
        -Math.trunc(formValues?.lastMinuteDiscountValue ?? NaN),
        !!dayPackage
      ));

  const isValid = !activateWithoutValidValue && !activateWithoutValue;

  const isNotEgalsToPrevious =
    isValid &&
    Math.trunc(lastMinuteAutoDiscountRate * 100) !==
      Math.trunc(formValues?.lastMinuteDiscountValue ?? NaN);

  const dayAreNotEgalsToPrevious =
    isValid && lastMinuteAutoDiscountDays !== formValues?.lastMinuteDays;
  const switchDifferentComparePrevious = initialDisplayValue !== activeRules;

  return (
    isValid &&
    isNotEgalsToPrevious &&
    dayAreNotEgalsToPrevious &&
    switchDifferentComparePrevious
  );
};
